"use client";

import { useTina, tinaField, coloredText, getIcon } from "@/lib/utils";
import Section from "@/components/Section";
import Image from "next/image";

import CallToAction from "@/components/CallToAction";
import { Button } from "@/components/ui/button";
import Pill from "@/components/Pill";
import { CheckCheck, Infinity } from "lucide-react";
import TextTooltip from "@/components/TextTooltip";
import Link from "@/components/ui/link";

export default function AboutPage(props) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const { header, exampleSections, callToAction } = data.about;
  return (
    <div>
      <Header header={header} />
      <AnyStackAnyUseCase exampleSections={exampleSections} />
      <CallToAction callToAction={callToAction} />
    </div>
  );
}

const Header = ({ header }) => {
  const title = coloredText(header?.title);
  return (
    <Section
      as="header"
      wrapperClassName="lg:!py-32"
      className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 items-center"
    >
      <div className="flex flex-col items-start gap-4">
        <h1
          className="h1 text-balance"
          data-tina-field={tinaField(header, "title")}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <TextTooltip
          text={header?.description}
          className="p-xl"
          data-tina-field={tinaField(header, "description")}
        />
        <Button
          data-tina-field={tinaField(header, "button")}
          className="mt-4"
          asChild
        >
          <Link href={header?.button?.link || "/"}>
            {header?.button?.label}
          </Link>
        </Button>
      </div>
      <div className="flex lg:justify-end items-stretch">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
          {header?.channels?.map((channel, index) => {
            const Icon = getIcon(channel.icon);
            return (
              <div
                key={index}
                className="w-full md:w-auto card bg-background-purplish flex flex-col"
              >
                {Icon && (
                  <Icon
                    className="w-9 h-9 text-primary-300"
                    data-tina-field={tinaField(channel, "icon")}
                  />
                )}
                <div className="flex-1 flex flex-col justify-end gap-2 mt-16">
                  <div
                    className="h4"
                    data-tina-field={tinaField(channel, "title")}
                  >
                    {channel.title}
                  </div>
                  <div
                    className="p text-foreground-300"
                    data-tina-field={tinaField(channel, "description")}
                  >
                    {channel.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

const AnyStackAnyUseCase = ({ exampleSections }) => {
  return (
    <Section
      wrapperClassName="bg-gradient-to-b from-background-purplish via-background-blackish to-background-purplish !pt-24 !pb-32 lg:!pt-32 lg:!pb-48"
      className="flex flex-col gap-32 lg:gap-64"
    >
      {exampleSections.map((section, index) => (
        <div key={index} className="flex flex-col md:flex-row gap-8">
          <div className="flex-1 md:sticky top-52 pb-8 lg:pb-24 max-md:flex  max-md:flex-col max-md:items-center max-md:text-center lg:self-start h-fit">
            <Pill data-tina-field={tinaField(section, "sectionTitle")}>
              {section.sectionTitle}
            </Pill>
            <h2
              className="h3 mt-5 mb-4"
              data-tina-field={tinaField(section, "title")}
            >
              {section.title}
            </h2>
            <TextTooltip
              text={section.description}
              className="p-lg max-w-[400px]"
              data-tina-field={tinaField(section, "description")}
            />
            <div className="mt-7 flex flex-col gap-5 items-center md:items-start">
              {section?.benefits?.map((benefit, benefitIndex) => {
                return (
                  <div key={benefitIndex} className="flex items-center gap-3">
                    <CheckCheck className="w-6 h-6 text-primary-300" />
                    <TextTooltip
                      text={benefit.label}
                      className="p-lg"
                      data-tina-field={tinaField(benefit, "label")}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-12">
            {section?.examples?.map((example, exampleIndex) => {
              const Icon = getIcon(example.icon);
              return (
                <div key={exampleIndex} className="card bg-background-blackish">
                  {Icon && (
                    <Icon
                      className="w-8 h-8 text-primary-300"
                      data-tina-field={tinaField(example, "icon")}
                    />
                  )}
                  <div
                    className="h5 mt-5 mb-2"
                    data-tina-field={tinaField(example, "title")}
                  >
                    {example.title}
                  </div>
                  <TextTooltip
                    text={example.description}
                    className="p-lg text-foreground-300"
                    data-tina-field={tinaField(example, "description")}
                  />
                </div>
              );
            })}

            {section?.examplesFooter && (
              <div className="flex justify-center">
                <p
                  className="p text-foreground-300 flex items-center gap-2"
                  data-tina-field={tinaField(section, "examplesFooter")}
                >
                  <Infinity className="w-7 h-7 text-primary-300" />
                  {section?.examplesFooter}
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </Section>
  );
};
